import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { Plus, Search, MoreVertical, Edit, Trash2, Eye, Calendar, Users, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { usePermissions } from '../context/PermissionsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_CATEGORIES = [
  { value: 'Office Event', label: 'Office Event' },
  { value: 'Sales Week', label: 'Sales Week' },
  { value: 'Outside Event', label: 'Outside Event' },
];

const getCategoryBadgeColor = (category) => {
  switch (category) {
    case 'Office Event': return 'bg-purple-100 text-purple-800';
    case 'Sales Week': return 'bg-green-100 text-green-800';
    case 'Outside Event': return 'bg-blue-100 text-blue-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

export default function EventsPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'Office Event',
    start_datetime: '',
    end_datetime: '',
    timezone: 'America/New_York',
    location: '',
    capacity: '',
    description: '',
    is_active: true
  });
  
  const { hasPermission, role } = usePermissions();
  const canCreate = hasPermission('events_create');
  const canUpdate = hasPermission('events_update');
  const canDelete = hasPermission('events_delete');
  const isReadOnly = role === 'top_management' || role === 'finance';

  useEffect(() => {
    fetchEvents();
  }, [showActiveOnly, categoryFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEvents = async () => {
    try {
      let url = `${API_URL}/api/events?`;
      if (showActiveOnly) url += 'active=true&';
      if (categoryFilter !== 'all') url += `category=${encodeURIComponent(categoryFilter)}`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      }
    } catch (error) {
      toast.error('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!formData.name || !formData.start_datetime) {
      toast.error('Name and start date/time are required');
      return;
    }

    try {
      const payload = {
        ...formData,
        capacity: formData.capacity ? parseInt(formData.capacity) : null,
        start_datetime: new Date(formData.start_datetime).toISOString(),
        end_datetime: formData.end_datetime ? new Date(formData.end_datetime).toISOString() : null
      };

      const response = await fetch(`${API_URL}/api/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Event created successfully');
        setIsCreateOpen(false);
        resetForm();
        fetchEvents();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to create event');
      }
    } catch (error) {
      toast.error('Failed to create event');
    }
  };

  const handleUpdateEvent = async () => {
    if (!formData.name || !formData.start_datetime) {
      toast.error('Name and start date/time are required');
      return;
    }

    try {
      const payload = {
        ...formData,
        capacity: formData.capacity ? parseInt(formData.capacity) : null,
        start_datetime: new Date(formData.start_datetime).toISOString(),
        end_datetime: formData.end_datetime ? new Date(formData.end_datetime).toISOString() : null
      };

      const response = await fetch(`${API_URL}/api/events/${selectedEvent.event_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Event updated successfully');
        setIsEditOpen(false);
        resetForm();
        fetchEvents();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update event');
      }
    } catch (error) {
      toast.error('Failed to update event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to deactivate this event?')) return;

    try {
      const response = await fetch(`${API_URL}/api/events/${eventId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Event deactivated');
        fetchEvents();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to deactivate event');
      }
    } catch (error) {
      toast.error('Failed to deactivate event');
    }
  };

  const openEditDialog = (event) => {
    setSelectedEvent(event);
    setFormData({
      name: event.name,
      category: event.category,
      start_datetime: event.start_datetime ? formatDateTimeLocal(event.start_datetime) : '',
      end_datetime: event.end_datetime ? formatDateTimeLocal(event.end_datetime) : '',
      timezone: event.timezone || 'America/New_York',
      location: event.location || '',
      capacity: event.capacity?.toString() || '',
      description: event.description || '',
      is_active: event.is_active
    });
    setIsEditOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'Office Event',
      start_datetime: '',
      end_datetime: '',
      timezone: 'America/New_York',
      location: '',
      capacity: '',
      description: '',
      is_active: true
    });
    setSelectedEvent(null);
  };

  const formatDateTimeLocal = (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toISOString().slice(0, 16);
    } catch {
      return '';
    }
  };

  const formatDisplayDate = (isoString) => {
    try {
      return format(new Date(isoString), 'MMM d, yyyy • h:mm a');
    } catch {
      return isoString;
    }
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = !searchQuery || 
      event.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.location?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // Create form content - render function to prevent focus loss on re-render
  const createEventFormContent = (onSubmit, submitLabel) => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Event Name *</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="e.g., January Office Event"
          data-testid="input-event-name"
        />
      </div>
      
      <div className="space-y-2">
        <Label>Category *</Label>
        <Select
          value={formData.category}
          onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
        >
          <SelectTrigger data-testid="select-event-category">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {EVENT_CATEGORIES.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Start Date & Time *</Label>
          <Input
            type="datetime-local"
            value={formData.start_datetime}
            onChange={(e) => setFormData(prev => ({ ...prev, start_datetime: e.target.value }))}
            data-testid="input-start-datetime"
          />
        </div>
        <div className="space-y-2">
          <Label>End Date & Time</Label>
          <Input
            type="datetime-local"
            value={formData.end_datetime}
            onChange={(e) => setFormData(prev => ({ ...prev, end_datetime: e.target.value }))}
            data-testid="input-end-datetime"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Location</Label>
          <Input
            value={formData.location}
            onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
            placeholder="e.g., NYC Office"
            data-testid="input-location"
          />
        </div>
        <div className="space-y-2">
          <Label>Capacity</Label>
          <Input
            type="number"
            value={formData.capacity}
            onChange={(e) => setFormData(prev => ({ ...prev, capacity: e.target.value }))}
            placeholder="Max attendees"
            data-testid="input-capacity"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Event details..."
          className="min-h-[80px]"
          data-testid="input-description"
        />
      </div>

      <div className="flex items-center gap-2">
        <Switch
          checked={formData.is_active}
          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
          data-testid="switch-is-active"
        />
        <Label>Active (visible for registration)</Label>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={() => { setIsCreateOpen(false); setIsEditOpen(false); resetForm(); }}>
          Cancel
        </Button>
        <Button onClick={onSubmit} className="bg-gold hover:bg-gold/90 text-slate-900" data-testid="submit-event-btn">
          {submitLabel}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="p-4 space-y-3" data-testid="events-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-heading">Events</h1>
          <p className="text-slate-500 text-sm mt-1">Manage office events, sales weeks, and outside events</p>
        </div>
        {canCreate && (
          <Button 
            onClick={() => { resetForm(); setIsCreateOpen(true); }}
            className="bg-gold hover:bg-gold/90 text-slate-900 h-7 px-3 text-xs"
            data-testid="create-event-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Create Event
          </Button>
        )}
      </div>

      {/* Filters - Compact utility bar */}
      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center bg-white border rounded-lg px-2 py-1.5 shadow-sm">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          <Input
            placeholder="Search events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-7 h-7 text-xs"
            data-testid="search-events"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-36 h-7 text-xs" data-testid="filter-category">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {EVENT_CATEGORIES.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <Switch
            checked={showActiveOnly}
            onCheckedChange={setShowActiveOnly}
            data-testid="filter-active-only"
            className="scale-90"
          />
          <Label className="text-xs whitespace-nowrap">Active Only</Label>
        </div>
      </div>

      {/* Events Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No events found</p>
              {canCreate && (
                <Button 
                  variant="link" 
                  onClick={() => { resetForm(); setIsCreateOpen(true); }}
                  className="text-gold mt-2"
                >
                  Create your first event
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Event</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-center">Registrations</TableHead>
                    <TableHead className="text-center">RSVP Yes</TableHead>
                    <TableHead className="text-center">Attended</TableHead>
                    <TableHead className="text-center">No Show</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEvents.map((event) => (
                    <TableRow 
                      key={event.event_id} 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/events/${event.event_id}`)}
                      data-testid={`event-row-${event.event_id}`}
                    >
                      <TableCell className="font-medium">{event.name}</TableCell>
                      <TableCell>
                        <Badge className={getCategoryBadgeColor(event.category)}>
                          {event.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDisplayDate(event.start_datetime)}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {event.location && (
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {event.location}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{event.total_members || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-green-100 text-green-800">{event.rsvp_yes || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-emerald-100 text-emerald-800">{event.attended || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-orange-100 text-orange-800">{event.no_shows || 0}</Badge>
                      </TableCell>
                      <TableCell>
                        {event.is_active ? (
                          <Badge className="bg-green-100 text-green-800">Active</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {!isReadOnly && (canUpdate || canDelete) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`event-menu-${event.event_id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/events/${event.event_id}`)}>
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              {canUpdate && (
                                <DropdownMenuItem onClick={() => openEditDialog(event)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit Event
                                </DropdownMenuItem>
                              )}
                              {canDelete && (
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteEvent(event.event_id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Deactivate
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Event Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Event</DialogTitle>
          </DialogHeader>
                {createEventFormContent(handleCreateEvent, "Create Event")}
        </DialogContent>
      </Dialog>

      {/* Edit Event Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Event</DialogTitle>
          </DialogHeader>
                {createEventFormContent(handleUpdateEvent, "Save Changes")}
        </DialogContent>
      </Dialog>
    </div>
  );
}
